// Effect 5 — Screenshake. Magnitude + decay, jittered from the SEEDED rng so a given seed shakes
// identically every replay (frame-strip capture depends on that). Applied to #stage, decoupled
// from the per-tile transforms so it composes with everything else.
import { feel } from "../feel";
import { forkRand } from "../../core/rng";
import { registerEffect } from "../registry";
import { isFrozen } from "../anim";
import { intensity, timed } from "../../tuner/scale";
import { reduceMotion, isCalm } from "../a11y";

let shaking = false;

// Screenshake jitters every rAF frame, so its draw count is framerate-dependent. It uses a PRIVATE
// forked stream (not the shared `rand`) so those per-frame draws can't shift personality/particle
// randomness downstream and break `?seed=` reproducibility. Its own shake stays deterministic.
const shakeRand = forkRand(0x5ca4e);
const shakeRange = (min: number, max: number): number => min + shakeRand() * (max - min);

export function registerScreenshake(): void {
  registerEffect(
    { name: "screenshake", label: "5 · Screenshake" },
    "word:valid",
    ({ word }) => {
      const stage = document.getElementById("stage");
      // shake is the classic vestibular trigger — skip under reduce-motion OR calm (welcome/mash)
      if (!stage || shaking || reduceMotion() || isCalm()) return;
      shaking = true;
      // Longer words hit a touch harder — scales with length, capped. Intensity scales magnitude.
      const peak =
        feel.screenshake.magnitude *
        intensity(feel.screenshake) *
        Math.min(1.8, 0.6 + word.length * 0.12);
      const start = performance.now();
      const dur = timed(feel.screenshake.decayMs);
      function frame(now: number) {
        const t = (now - start) / dur;
        if (t >= 1) {
          stage!.style.transform = "";
          shaking = false;
          return;
        }
        if (!isFrozen()) {
          const amp = peak * (1 - t); // linear decay
          const dx = shakeRange(-amp, amp);
          const dy = shakeRange(-amp, amp);
          stage!.style.transform = `translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px)`;
        }
        requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    },
  );
}
